import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { Spinner } from '@shared/components/Spinner';
import { lastNameFirstNameCommaDelimited } from '@shared/utils/formatName';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { buildPathWithSearchParams } from '@shared/utils/urlUtils';
import cx from 'classnames';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  generatePath,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import typography from '~styles/typography.scss';
import { GUEST_BOOK_GUEST_PATH } from '../paths';
import {
  getGuest,
  type GuestReservation,
  type GuestsApiGuest,
} from './apiHelpers';
import { GuestBookFilters } from './GuestBookFilters';
import styles from './GuestBookPage.scss';
import { GuestDetails } from './GuestDetails';
import { GuestReservationDetailsDrawer } from './GuestReservationDetailsDrawer';
import { GuestSearchBar } from './GuestSearchBar';
import { GuestSelectionCheckbox } from './GuestSelectionCheckbox';
import {
  guestBookGuestQueryKey,
  useGuestBookFilterParams,
  useRestaurantGuests,
} from './hooks';
import { MultiSelectDetails } from './MultiSelectDetails';

const AllGuestsLoadedIndicator = () => (
  <span className={styles.endMessage}>(つ▀¯▀)つ No more guests</span>
);

const LoadingSpinner = () => (
  <span className={styles.spinnerContainer}>
    <Spinner className={styles.spinner} />
  </span>
);

const NoGuestsMessage = () => (
  <p>No guests found. Try adjusting your filters or search criteria.</p>
);

const GuestDetailsLoadError = ({ onBack }: { onBack: () => void }) => (
  <div className={styles.guestDetailsPanel}>
    <p>We could not load this guest.</p>
    <Button
      label="Back to list"
      onClick={onBack}
      variant={ButtonVariants.Tertiary}
    />
  </div>
);

const MINIMUM_SEARCH_CHARS = 3;
const DEBOUNCE_SEARCH_MS = 250;

export const GuestBookPage = () => {
  const { id: restaurantId } = useRestaurant();
  const { guestId } = useParams<{ guestId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isMultiSelectingGuests, setIsMultiSelectingGuests] = useState(false);
  const [multiSelectedGuests, setMultiSelectedGuests] = useState<
    Record<string, boolean>
  >({});

  const [selectedGuestReservation, setSelectedGuestReservation] =
    useState<GuestReservation>();

  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchQuery = useDebouncedValue(
    searchText,
    DEBOUNCE_SEARCH_MS,
  );
  const guestListContainerId = useId();

  const { hasActiveFilters } = useGuestBookFilterParams();
  const { data, fetchNextPage, hasNextPage, isPending } = useRestaurantGuests({
    search: debouncedSearchQuery,
  });

  const guests = data?.pages.flat() || [];

  const navigateToGuest = (id: string) => {
    navigate(
      buildPathWithSearchParams(
        generatePath(GUEST_BOOK_GUEST_PATH, { guestId: id }),
        searchParams,
      ),
    );
  };

  const navigateClearGuest = () => {
    navigate(
      buildPathWithSearchParams(
        generatePath(GUEST_BOOK_GUEST_PATH, { guestId: null }),
        searchParams,
      ),
    );
  };

  const selectedGuestFromList = guestId
    ? guests.find((g) => g.id === guestId)
    : undefined;

  const { data: guestFromIdQuery, isError: isGuestByIdError } = useQuery({
    queryKey: guestBookGuestQueryKey(restaurantId, guestId!),
    queryFn: () => getGuest(restaurantId, guestId!),
    enabled: Boolean(guestId && !selectedGuestFromList && !isPending),
  });

  const selectedGuest: GuestsApiGuest | undefined =
    selectedGuestFromList ?? guestFromIdQuery;

  useEffect(() => {
    setSelectedGuestReservation(undefined);
  }, [guestId]);

  // Clear the guestId from the URL when any filter param changes so the guest
  // details panel doesn't linger for a guest who may not match the new filter.
  const filterSignature = [
    searchParams.getAll('tag').sort().join(','),
    searchParams.getAll('vipTierId').sort().join(','),
    searchParams.get('startDate') ?? '',
    searchParams.get('endDate') ?? '',
    searchParams.get('marketingOptedInOnly') ?? '',
  ].join('|');
  const previousFilterSignatureRef = useRef(filterSignature);
  useEffect(() => {
    if (previousFilterSignatureRef.current === filterSignature) return;
    previousFilterSignatureRef.current = filterSignature;
    if (guestId) {
      navigateClearGuest();
    }
    // navigateClearGuest depends on searchParams/navigate — omitting it
    // intentionally so this fires only on filter-signature transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature, guestId]);

  const showGuestDetailError = Boolean(
    guestId && !selectedGuestFromList && !isPending && isGuestByIdError,
  );

  const showGuestDetailsLoading =
    Boolean(guestId) && !selectedGuest && !showGuestDetailError;

  const handleSetGuestSearchQuery = (query: string) => {
    if (query.length < MINIMUM_SEARCH_CHARS) {
      setSearchText('');
      return;
    }

    setSearchText(query);
  };

  const handleGuestReservationClick = (reservation: GuestReservation) => {
    setSelectedGuestReservation(reservation);
  };
  const handleGuestReservationDetailsDrawerClose = () => {
    setSelectedGuestReservation(undefined);
  };

  const handleMultiSelectToggle = () => {
    if (!isMultiSelectingGuests && guestId) {
      setMultiSelectedGuests({ [guestId]: true });
      navigateClearGuest();
    } else if (isMultiSelectingGuests) {
      setMultiSelectedGuests({});
    }
    setIsMultiSelectingGuests(!isMultiSelectingGuests);
  };

  const isGuestSelected = (id: string): boolean => {
    return (
      (!isMultiSelectingGuests && guestId === id) ||
      Boolean(multiSelectedGuests[id])
    );
  };

  const renderGuestDetailsPanel = () => {
    if (isMultiSelectingGuests) {
      return (
        <MultiSelectDetails
          selectedGuestIds={deriveListOfMultiSelectionGuestIds(
            multiSelectedGuests,
          )}
        />
      );
    }
    if (showGuestDetailError) {
      return <GuestDetailsLoadError onBack={navigateClearGuest} />;
    }
    if (showGuestDetailsLoading) {
      return (
        <div className={styles.guestDetailsLoading}>
          <Spinner className={styles.guestDetailsSpinner} />
        </div>
      );
    }
    return (
      <GuestDetails
        guest={selectedGuest}
        handleGuestReservationClick={handleGuestReservationClick}
        onClose={navigateClearGuest}
        selectedGuestReservationId={selectedGuestReservation?.id}
      />
    );
  };

  return (
    <>
      <PageHeader title="Guest Book">
        {guests.length > 1 && (
          <Button
            label={
              isMultiSelectingGuests
                ? 'Cancel Selection'
                : 'Multi-Select Guests'
            }
            onClick={handleMultiSelectToggle}
            variant={ButtonVariants.Tertiary}
          />
        )}
      </PageHeader>
      <PageContent
        className={cx(styles.pageContent, {
          [styles.multiSelectionView]: isMultiSelectingGuests,
        })}
      >
        <div className={styles.container}>
          <div
            id={guestListContainerId}
            data-testid="guest-list-container"
            className={styles.guestList}
          >
            <div className={styles.searchBar}>
              <GuestSearchBar onSearch={handleSetGuestSearchQuery} />
              <GuestBookFilters search={debouncedSearchQuery} />
            </div>
            {guests.length === 0 &&
              (hasActiveFilters || debouncedSearchQuery) && <NoGuestsMessage />}
            {!!guests.length && (
              <ul data-testid="guest-list">
                <InfiniteScroll
                  dataLength={guests.length}
                  hasMore={hasNextPage}
                  loader={<LoadingSpinner />}
                  next={fetchNextPage}
                  scrollableTarget={guestListContainerId}
                  style={{ overflow: 'unset' }}
                  endMessage={<AllGuestsLoadedIndicator />}
                >
                  {guests.map((guest) => (
                    <li
                      key={guest.id}
                      className={cx(styles.guestListItem, {
                        [styles.guestListItemSelected]: isGuestSelected(
                          guest.id,
                        ),
                        [styles.guestListItemVip]: guest.hasVipTier,
                        [styles.guestListItemSelectedVip]:
                          guest.hasVipTier && isGuestSelected(guest.id),
                      })}
                    >
                      {isMultiSelectingGuests && (
                        <GuestSelectionCheckbox
                          // TODO: How should we style the checkbox label?
                          id={guest.id}
                          className={styles.guestSelectionCheckbox}
                          checked={Boolean(multiSelectedGuests[guest.id])}
                          onChange={(checked) => {
                            const updatedState = {
                              ...multiSelectedGuests,
                              [guest.id]: checked,
                            };

                            setMultiSelectedGuests(updatedState);
                          }}
                        >
                          <span
                            className={cx(typography.h5, styles.guestButton, {
                              [styles.guestButtonVip]: guest.hasVipTier,
                            })}
                          >
                            {guest.isVip && (
                              <Icon
                                ariaLabel="Is a VIP"
                                className={styles.vipIcon}
                                name="crown"
                                role="img"
                                fill="var(--gold)"
                              />
                            )}
                            {lastNameFirstNameCommaDelimited(
                              guest.lastName,
                              guest.firstName,
                            )}
                            {guest.hasVipTier && (
                              <span className={styles.vipTierBadge}>VIP</span>
                            )}
                          </span>
                        </GuestSelectionCheckbox>
                      )}

                      {!isMultiSelectingGuests && (
                        <button
                          className={cx(typography.h5, styles.guestButton, {
                            [styles.guestButtonVip]: guest.hasVipTier,
                          })}
                          onClick={() => {
                            navigateToGuest(guest.id);
                          }}
                          type="button"
                        >
                          {guest.isVip && (
                            <Icon
                              ariaLabel="Is a VIP"
                              className={styles.vipIcon}
                              name="crown"
                              role="img"
                              fill="var(--gold)"
                            />
                          )}
                          {lastNameFirstNameCommaDelimited(
                            guest.lastName,
                            guest.firstName,
                          )}
                          {guest.hasVipTier && (
                            <span className={styles.vipTierBadge}>VIP</span>
                          )}
                        </button>
                      )}
                    </li>
                  ))}
                </InfiniteScroll>
              </ul>
            )}
          </div>
          {renderGuestDetailsPanel()}
        </div>
        {selectedGuestReservation && (
          <GuestReservationDetailsDrawer
            onClose={handleGuestReservationDetailsDrawerClose}
            guestReservation={selectedGuestReservation}
          />
        )}
      </PageContent>
    </>
  );
};

const deriveListOfMultiSelectionGuestIds = (
  multiSelectedGuests: Record<string, boolean>,
): string[] => {
  return Object.keys(multiSelectedGuests).filter(
    (guestId) => multiSelectedGuests[guestId],
  );
};
